import { Popup, PopupArrow } from '@cian/popup-component';
import { IStyleConfig, mergeStyles } from '@cian/utils';
import * as PropTypes from 'prop-types';
import * as React from 'react';

import { ICommercialCardContext } from '..';
import { AddressPathItem, TAddressPathItem } from '../../common/components/address_path_item';
import { ReliableIcon } from '../../common/components/reliable_icon';
import { SuggestionAddressPathItem } from '../../common/components/suggestion_address_path_item';
import { Underground } from '../../common/components/underground';
import { UndergroundIcon } from '../../common/components/underground_icon';
import { MICRODATA_EXPERIMENT_ALL_GROUPS, MICRODATA_EXPERIMENT_NAME } from '../../experiments/experiment';
import { ELocationTypeId, ICoworking } from '../../types/offer';
import { Coworking } from '../Coworking';
import { getUnderground } from '../../utils/getUnderground';

const style = require('./Address.css');

interface IAddressProps {
  containerStyle?: IStyleConfig;
  qsToUris?: { [key: string]: string; };
  currentPath: string | undefined;
  suggestionDistancesSeoText: string | undefined;
  onJKLinkClicked(): void;
  onPageJumped(label?: string): void;
  coworking?: ICoworking;
}

export class Address extends React.Component<IAddressProps> {
  public context: ICommercialCardContext;

  public static contextTypes = {
    api: PropTypes.object,
    experiments: PropTypes.array,
    isSimilar: PropTypes.bool,
    jsonQuery: PropTypes.object,
    offer: PropTypes.object,
    offerHelper: PropTypes.object,
  };

  public render() {
    const { offerHelper } = this.context;
    const { containerStyle } = this.props;

    return (
      <div {...mergeStyles(style['address'], containerStyle) }>
        {this.renderAreaBlock()}
        {this.renderBuildingUrl()}
        {this.renderAddressPath()}
        {!offerHelper.isPrimaryMetroRegion() && this.renderRegionUnderground()}
      </div>
    );
  }

  private renderAreaBlock = () => {
    const { offer, offerHelper } = this.context;
    const { geo, fullUrl } = offer;
    const underground = offerHelper.isPrimaryMetroRegion() ? getUnderground(offer) : undefined;

    if (underground) {
      const locationId = geo && geo.address.length > 0 && geo.address[0].id || undefined;

      return (
        <Underground
          underground={underground}
          locationId={locationId}
          url={fullUrl}
          onClick={this.props.onPageJumped.bind(null, 'card_header_in_newlist')}
        />
      );
    } else if (geo) {
      const location = [...geo.address].reverse().find((address) => {
        return address.type === 'location';
      });

      if (location) {
        return (<a
          href={fullUrl}
          target="_blank"
          className={style['header-link']}
          onClick={this.props.onPageJumped.bind(null, 'card_header_in_newlist')}
        >
          <div className={style['header']}>{location.fullName}</div>
        </a>);
      }
    }

    return null;
  }

  private renderBuildingUrl() {
    const { geo, businessShoppingCenter, newbuilding, coworking } = this.context.offer;
    const isReliable = newbuilding && newbuilding.showJkReliableFlag;

    if (coworking) {
      return <Coworking {...coworking} />;
    }

    const reliableIcon = (href: string) => (isReliable && (
      <div className={style['reliable-wrapper']}>
        <ReliableIcon className={style['reliable-icon']} />

        <Popup arrow={PopupArrow.LEFT} containerStyle={style['popup']}>
          <div>
            <ReliableIcon className={style['icon']} />
          </div>
          <strong className={style['title']}>Проверено ЦИАН</strong>
          <p>
            В ЖК есть корпуса, проверенные ЦИАН.
          </p>
          <a href={`${href}#reliable`} className={style['link']} target="_blank">Узнать подробнее</a>
        </Popup>
      </div>
    ));

    if (businessShoppingCenter && businessShoppingCenter.type &&
      ((businessShoppingCenter.useParentName && businessShoppingCenter.parentName) ||
        (!businessShoppingCenter.useParentName && businessShoppingCenter.name))
    ) {
      const bsCenterTypeName = {
        businessCenter: 'БЦ',
        shoppingCenter: 'ТЦ',
        warehouse: 'СК',
      };

      const name = `${bsCenterTypeName[businessShoppingCenter.type]} «${!businessShoppingCenter.useParentName ?
        businessShoppingCenter.name : businessShoppingCenter.parentName}»`;
      const url = !businessShoppingCenter.useParentName ?
        businessShoppingCenter.fullUrl : businessShoppingCenter.parentUrl;

      return (
        <div className={style['building']}>
          {!url && name}
          {url &&
            <div>
              {reliableIcon(url)}

              <a href={url} className={style['building-link']}>
                {name}
              </a>
            </div>
          }
        </div>
      );
    } else if (geo && geo.jk && geo.jk.displayName) {
      const { fullUrl } = geo.jk;

      return (
        <div className={style['building']}>
          {!fullUrl && geo.jk.displayName}
          {fullUrl &&
            <div>
              {reliableIcon(fullUrl)}

              <a href={fullUrl} onClick={this.props.onJKLinkClicked} className={style['building-link']}>
                {geo.jk.displayName}
              </a>
            </div>
          }
        </div>
      );
    }

    return null;
  }

  private renderAddressPath = () => {
    const {
      offer,
      jsonQuery,
      api: { tracking },
    } = this.context;

    const {
      currentPath,
      qsToUris,
      suggestionDistancesSeoText,
    } = this.props;

    const { geo, suggestionDistances } = offer;
    if (!geo) {
      return null;
    }

    const additionalStyle = geo.jk || offer.businessShoppingCenter ? { paddingTop: 0 } : undefined;
    const undergroundStation = getUnderground(offer);

    let cityIndex = 1;
    let isGeoAddressWithMetro = false;

    geo.address.forEach(({ locationTypeId, type }, index) => {
      /**
       * Если в geo.address уже есть метро, то вручную
       * на фронте его подставлять не нужно
       */
      if (type === 'metro') {
        isGeoAddressWithMetro = true;
      }

      if (locationTypeId === ELocationTypeId.City) {
        cityIndex = index + 1;
      }
    });

    let addressLine = geo.address.map(({ fullName, qs, type }) => ({
      label: fullName,
      qs,
      type: type === 'metro' ? 'metro' : 'address',
      url: qsToUris && qsToUris[qs],
    })) || [];

    /**
     * Если есть undergroundStation, но нет метро внутри geo.address,
     * то добавляем метро вручную тут
     */
    if (undergroundStation && !isGeoAddressWithMetro) {
      addressLine.splice(cityIndex, 0, {
        label: `м.\u00A0${undergroundStation.name}`,
        qs: undergroundStation.qs,
        type: 'metro',
        url: qsToUris && qsToUris[undergroundStation.qs],
      });
    }

    /**
     * Пытаемся взять гео ID текущей выдачи
     */
    const jsonQueryGeoId = jsonQuery.geo && jsonQuery.geo.value.length === 1
      // tslint:disable-next-line:no-any
      ? (jsonQuery.geo.value[0] as any).id
      : undefined;

    /**
     * Если в гео карточки уже есть гео запроса из ЧПУ - дополнительно расстояние
     * от текущего гео ЧПУ выводить не нужно
     */
    const geoIdConcurrences = jsonQueryGeoId
      ? geo.address.find(({ id }) => id === jsonQueryGeoId)
      : false;
    const isVisibleSuggestSeoText = geoIdConcurrences === undefined;

    const addressString = addressLine
      .map(address => address.label)
      .join(', ');

    const microData = MICRODATA_EXPERIMENT_ALL_GROUPS.includes(this.getExperimentGroup())
      ? {
        itemScope: true,
        itemType: 'http://schema.org/Place',
      }
      : {};

    return (
      <div {...microData} {...mergeStyles(style['address-path'], additionalStyle) }>
        <span {...{ itemProp: 'name', content: addressString }} />
        {addressLine.map(({ label, qs, type, url }, index) => {
          const item = <AddressPathItem
            key={`address-path-item-${index}`}
            label={label}
            qs={qs}
            url={url}
            type={type as TAddressPathItem}
            trackAddressClick={tracking && tracking.trackAddressClick}
          />;

          return [index > 0 && ', ', item];
        })}
        {
          (isVisibleSuggestSeoText && suggestionDistancesSeoText) &&
            <SuggestionAddressPathItem
              currentPath={currentPath}
              text={suggestionDistancesSeoText}
              distance={suggestionDistances}
            />
        }
      </div>
    );
  }

  private renderRegionUnderground = () => {
    const { offer } = this.context;

    if (!offer.geo) {
      return null;
    }

    const undergroundStation = getUnderground(offer);

    if (!undergroundStation) {
      return null;
    }

    return (
      <div className={style['region-metro']}>
        <UndergroundIcon color={`#${undergroundStation.lineColor}`} width={9} height={9} /> {undergroundStation.name}
      </div>
    );
  }

  private getExperimentGroup() {
    return this.context.experiments.reduce((group, experiment) => {
      let abGroup = group;

      if (experiment.name === MICRODATA_EXPERIMENT_NAME) {
        abGroup = Number(experiment.abGroup);
      }

      return abGroup;
    }, 0);
  }
// tslint:disable-next-line:max-file-line-count
}
